import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Search } from "lucide-react";

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [style, setStyle] = useState("");
  const [year, setYear] = useState("");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (category) params.set("category", category);
    if (style) params.set("style", style);
    if (year) params.set("year", year);
    if (sort) params.set("sort", sort);
    api.get(`/projects?${params.toString()}`).then((r) => setItems(r.data));
  }, [q, category, style, year, sort]);

  const styles = useMemo(() => Array.from(new Set(items.map((i) => i.style).filter(Boolean))), [items]);
  const years = useMemo(() => Array.from(new Set(items.map((i) => i.year).filter(Boolean))).sort((a, b) => b - a), [items]);

  return (
    <div data-testid="page-portfolio" className="bg-[#F9F8F6]">
      <section className="container-editorial pt-16 md:pt-24 pb-10">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#7A736C] mb-6">Projects</div>
        <h1 className="font-serif text-5xl md:text-7xl lg:text-[84px] tracking-tight leading-[1] max-w-4xl">Selected work from the Hiraya studio.</h1>
      </section>

      <section className="container-editorial pb-8 border-b border-[#D5D0C8]">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 bg-white border border-[#D5D0C8] px-4 py-2 rounded-sm">
            <Search size={16} className="text-[#7A736C]" />
            <input data-testid="portfolio-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…" className="bg-transparent text-sm outline-none w-56" />
          </div>
          <select data-testid="portfolio-filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className="bg-white border border-[#D5D0C8] px-4 py-2 rounded-sm text-sm">
            <option value="">All categories</option>
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>
          <select data-testid="portfolio-filter-style" value={style} onChange={(e) => setStyle(e.target.value)} className="bg-white border border-[#D5D0C8] px-4 py-2 rounded-sm text-sm">
            <option value="">All styles</option>
            {styles.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="portfolio-filter-year" value={year} onChange={(e) => setYear(e.target.value)} className="bg-white border border-[#D5D0C8] px-4 py-2 rounded-sm text-sm">
            <option value="">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select data-testid="portfolio-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-[#D5D0C8] px-4 py-2 rounded-sm text-sm ml-auto">
            <option value="newest">Newest</option>
            <option value="featured">Featured</option>
          </select>
        </div>
      </section>

      <section className="container-editorial py-12 md:py-16">
        {items.length === 0 && <div className="text-[#7A736C]">No projects match. Try adjusting filters.</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.map((p, i) => (
            <Link key={p.id} to={`/portfolio/${p.slug}`} data-testid={`portfolio-card-${i}`} className="group relative overflow-hidden rounded-sm aspect-[4/5]">
              <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1918]/90 via-[#1A1918]/20 to-transparent" />
              <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end text-[#F9F8F6]">
                <div className="text-[10px] uppercase tracking-[0.28em] opacity-90">{p.location} · {p.year}</div>
                <div className="font-serif text-2xl md:text-3xl mt-2 tracking-tight">{p.title}</div>
                <div className="text-xs mt-2 opacity-90">{p.style}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
